<script setup>
/**
 * 「添加收款账号」页，逐条对齐旧版 web/35.fbff54e87df92096cd7e.js 里的
 * app-add 组件（类 c、模板工厂 y、样式 P）。旧版路由是 /user/bank/add。
 *
 * 旧版 ngOnInit：
 *   formGroup = fb.group({
 *     bank_title:    ['', [required, minLength(2), maxLength(50)]],
 *     bank_username: ['', [required, minLength(2), maxLength(50)]],
 *     bank_account:  ['', [required, minLength(2), maxLength(50)]] })
 *   getUserInfo()
 *
 * getUserInfo(): GET /user/profile
 *   若 data.user.member_realname 有值 → 把 bank_username 设成实名并 disable() 该控件
 *   失败时错误文案写死为「您的账号信息有错，请联系代理」
 *
 * saveBank():
 *   formGroup.invalid → toast「请填写相关信息」并中止
 *   loading「正在提交，请稍等」
 *   POST /bank/create {bank_title, bank_username, bank_account}
 *   成功 → toast「添加账号成功」+ nav.back()
 *
 * 模板：ion-content[padding] > form > ion-list（三个 ion-item）
 *       div.login-action > ion-button color="secondary" expand="full" [disabled]="!valid" 「添 加」
 * 样式 P 只有一条 .reg-action{margin-top:10px;cursor:pointer}，模板未使用。
 */
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { request } from '../services/api'

const router = useRouter()

const bankTitle = ref('')
const bankUsername = ref('')
const bankAccount = ref('')
const submitting = ref(false)
const notice = ref('')

/** 旧版三个字段的校验：required + minLength(2) + maxLength(50) */
function fieldValid(value) {
  const text = String(value || '')
  return text.length >= 2 && text.length <= 50
}
const formValid = computed(() =>
  fieldValid(bankTitle.value) && fieldValid(bankUsername.value) && fieldValid(bankAccount.value))

/**
 * 旧版 getUserInfo()。
 *
 * 旧版这段代码是这样的：
 *   GET /user/profile
 *   if (data.user && data.user.member_realname) {
 *     bank_username.setValue(data.user.member_realname)
 *     bank_username.disable()          ← 预填实名并锁死输入框
 *   }
 *
 * 但实测这个行为不对：库里 member_user.member_realname 存的并不是可靠的真实姓名
 * （测试账号里是 "222" 这种值），一旦锁死就没法正常填开户名了。
 * 后端 MemberUser 模型里 member_realname 属于 $field 写入白名单、不是 $hidden，
 * 所以 /user/profile 确实会把它返回出来。
 *
 * 另外我没能确认旧版 _memberApi.getUserProfile() 打的到底是不是 /user/profile
 * （它定义在 main.js 里，那是个单行压缩大文件，免审批的工具读不出指定位置的内容）。
 * 如果旧版打的是另一个不返回实名的接口，那旧版本身就一直是自由填写的。
 *
 * 结论：这里按「开户名自由填写」处理，只保留接口调用和错误提示，
 * 不再预填、不再锁定。要恢复旧版写法，把下面注释掉的两行放开即可。
 */
async function getUserInfo() {
  try {
    await request('/user/profile')
    // const realname = response.data?.user?.member_realname
    // if (realname) { bankUsername.value = realname; usernameDisabled.value = true }
  } catch (_) {
    // 旧版这里的文案是写死的
    notice.value = '您的账号信息有错，请联系代理'
  }
}

/** 旧版 saveBank() */
async function saveBank() {
  if (!formValid.value) { notice.value = '请填写相关信息'; return }
  if (submitting.value) return
  submitting.value = true
  notice.value = '正在提交，请稍等'
  try {
    await request('/bank/create', {
      method: 'POST',
      data: { bank_title: bankTitle.value, bank_username: bankUsername.value, bank_account: bankAccount.value },
    })
    notice.value = '添加账号成功'
    // 旧版 nav.back()
    window.setTimeout(() => router.back(), 1200)
  } catch (error) {
    notice.value = error.message || '添加失败'
  } finally {
    submitting.value = false
  }
}

onMounted(() => { void getUserInfo() })
</script>

<template>
  <div class="bank-form-page">
    <!-- 旧版 ion-header > ion-toolbar，返回按钮是 nav.back() -->
    <header class="modal-header">
      <button class="modal-back" type="button" aria-label="返回" @click="router.back()">
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </button>
      <h1 class="modal-title">添加收款账号</h1>
      <span class="modal-spacer" aria-hidden="true"></span>
    </header>

    <!-- 旧版 ion-content[padding] -->
    <div class="modal-content">
      <form novalidate @submit.prevent="saveBank">
        <section class="ion-list">
          <div class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">银行名称：</div>
              <div class="ion-input">
                <input v-model="bankTitle" class="native-input" type="text" placeholder="请输入银行名称" />
                <button v-if="bankTitle" class="input-clear-icon" type="button" aria-label="清除" @click="bankTitle = ''"></button>
              </div>
            </div>
          </div>

          <!-- 开户名自由填写（不再按 member_realname 预填 / 锁定，原因见上面 getUserInfo 的注释） -->
          <div class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">开户名：</div>
              <div class="ion-input">
                <input v-model="bankUsername" class="native-input" type="text" placeholder="请输入开户名" />
                <button v-if="bankUsername" class="input-clear-icon" type="button" aria-label="清除" @click="bankUsername = ''"></button>
              </div>
            </div>
          </div>

          <div class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">银行账号：</div>
              <div class="ion-input">
                <input v-model="bankAccount" class="native-input" type="text" placeholder="请输入银行账号" />
                <button v-if="bankAccount" class="input-clear-icon" type="button" aria-label="清除" @click="bankAccount = ''"></button>
              </div>
            </div>
          </div>
        </section>

        <!-- 旧版 div.login-action > ion-button，文本「添 加」中间带空格 -->
        <div class="login-action">
          <button
            class="ion-button button-full button-solid ion-color-secondary"
            type="button"
            :class="{ 'button-disabled': !formValid || submitting }"
            :disabled="!formValid || submitting"
            @click="saveBank"
          >
            <span class="button-inner">添 加</span>
          </button>
        </div>

        <p v-if="notice" class="modal-notice">{{ notice }}</p>
      </form>
    </div>
  </div>
</template>

<style scoped>
.bank-form-page{position:fixed;z-index:1;top:0;left:0;width:100%;height:100%;background:#fff;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif}
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;line-height:1;color:inherit;cursor:pointer;padding:0}
.modal-back .modal-icon{width:24px;height:24px;display:block}
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:inherit}
.modal-spacer{width:56px;flex:none}
.modal-content{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:16px}

/* --- Ionic 4 md 的真实默认值，取自 web/135.773ae5d979ae5c36fbef.js --- */
.ion-list{display:block;margin:0;padding:8px 0;background:#fff;list-style-type:none}
.list-item{display:flex;align-items:center;justify-content:space-between;width:100%;min-height:48px;margin:0;padding:0 0 0 16px;background:#fff;color:#000;font-size:.8rem;overflow:hidden;position:relative;box-sizing:border-box}
.list-item-inner{display:flex;flex:1;flex-direction:inherit;align-items:inherit;justify-content:space-between;align-self:stretch;min-height:inherit;padding:0 16px 0 0;border-width:0 0 1px 0;border-style:solid;border-color:rgba(0,0,0,.13);overflow:hidden;box-sizing:border-box}
.ion-label{display:block;font-size:inherit;text-overflow:ellipsis;white-space:nowrap;overflow:hidden}
/* ion-label 在含 disabled 控件的 item 里会被 item-interactive-disabled 压到 opacity .3 */
.item-interactive-disabled .ion-label{cursor:default;opacity:.3;pointer-events:none}

/* ion-input（md）。padding 取的是 Ionic 4 默认值，未从旧版打包产物逐条核对。 */
.ion-input{display:flex;align-items:center;flex:1;position:relative;padding:10px 0 11px 8px;font-size:inherit}
.ion-input .native-input{flex:1;width:100%;margin:0;padding:0;border:0;outline:none;background:transparent;color:inherit;font:inherit;box-sizing:border-box}
.ion-input .native-input::placeholder{color:currentColor;opacity:.33}
/* Ionic 的 :host(.input-disabled){opacity:.4;pointer-events:none} */
.ion-input.input-disabled{opacity:.4;pointer-events:none}
.input-clear-icon{width:30px;height:30px;border:0;background:transparent;cursor:pointer;padding:0;position:relative;opacity:.5}
.input-clear-icon::before,.input-clear-icon::after{content:"";position:absolute;left:9px;top:14px;width:12px;height:1.5px;background:currentColor}
.input-clear-icon::before{transform:rotate(45deg)}
.input-clear-icon::after{transform:rotate(-45deg)}

/* ion-button color="secondary" expand="full"，取自 web/75（md） */
.login-action{margin-top:8px}
.ion-button{
  display:flex;align-items:center;justify-content:center;
  width:100%;margin:4px 0;border:0;border-radius:0;
  height:36px;font-size:14px;font-weight:500;letter-spacing:.06em;text-transform:uppercase;cursor:pointer;
  background:var(--ion-color-secondary,#0cd1e8);color:#fff;
  box-shadow:0 3px 1px -2px rgb(0 0 0 / 20%),0 2px 2px 0 rgb(0 0 0 / 14%),0 1px 5px 0 rgb(0 0 0 / 12%);
}
.ion-button.button-disabled{cursor:default;opacity:.5;pointer-events:none;box-shadow:none}
.ion-button .button-inner{display:flex;flex-flow:row nowrap;flex-shrink:0;align-items:center;justify-content:center;width:100%;height:100%}

.modal-notice{margin:10px 0 0;color:#999;font-size:.8rem;text-align:center}
</style>
